/**
 * EPUB Type Plugin for Milkdown
 *
 * Automatically manages epub:type attributes on H1 headings based on document type.
 * - Automatically adds {epub:type=X .X} to H1 for frontmatter/backmatter documents
 * - Prevents removal of epub:type attribute (users can only add styles)
 * - Allows adding .page-end class for page breaks
 */

import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import { $prose } from '@milkdown/kit/utils';
import type { DocumentType } from '../types';

// Mapping from document type to epub:type value
export const DOCUMENT_TYPE_TO_EPUB_TYPE: Record<string, string> = {
  // Frontmatter types
  'prologue': 'prologue',
  'abstract': 'abstract',
  'acknowledgments': 'acknowledgments',
  'copyright-page': 'copyright-page',
  'dedication': 'dedication',
  'credits': 'credits',
  'keywords': 'keywords',
  'imprint': 'imprint',
  'contributors': 'contributors',
  'other-credits': 'other-credits',
  'errata': 'errata',
  'revision-history': 'revision-history',
  'titlepage': 'titlepage',
  'halftitlepage': 'halftitlepage',
  'seriespage': 'seriespage',
  'foreword': 'foreword',
  'preface': 'preface',
  'frontispiece': 'frontispiece',
  'introduction': 'introduction',
  // Bodymatter types
  'part': 'part',
  // Backmatter types
  'appendix': 'appendix',
  'colophon': 'colophon',
  'bibliography': 'bibliography',
  'index': 'index',
  'conclusion': 'conclusion',
  'annexe': 'appendix', // Map to appendix
  'postface': 'afterword',
};

// Document types that require epub:type
export const TYPES_REQUIRING_EPUB_TYPE = new Set([
  // Frontmatter
  'prologue', 'abstract', 'acknowledgments', 'copyright-page', 'dedication',
  'credits', 'keywords', 'imprint', 'contributors', 'other-credits',
  'errata', 'revision-history', 'titlepage', 'halftitlepage', 'seriespage',
  'foreword', 'preface', 'frontispiece', 'introduction',
  // Bodymatter
  'part',
  // Backmatter
  'appendix', 'colophon', 'bibliography', 'index', 'conclusion', 'annexe', 'postface'
]);

// Bodymatter types (no epub:type required)
export const BODYMATTER_TYPES = new Set(['chapter']);

/**
 * Check if a document type requires epub:type attribute
 */
export function requiresEpubType(documentType: string | null | undefined): boolean {
  if (!documentType) return false;
  return TYPES_REQUIRING_EPUB_TYPE.has(documentType);
}

/**
 * Get the epub:type value for a document type
 */
export function getEpubType(documentType: string | null | undefined): string | null {
  if (!documentType) return null;
  return DOCUMENT_TYPE_TO_EPUB_TYPE[documentType] || null;
}

/**
 * Generate the attribute string for a document type
 * Example: {epub:type=bibliography .bibliography}
 */
export function generateAttributeString(
  documentType: string,
  additionalClasses: string[] = [],
  hasPageEnd: boolean = false
): string {
  const epubType = getEpubType(documentType);
  if (!epubType) return '';

  const classes = [epubType, ...additionalClasses];
  if (hasPageEnd) {
    classes.push('page-end');
  }

  const classStr = classes.map(c => `.${c}`).join(' ');
  return `{epub:type=${epubType} ${classStr}}`;
}

/**
 * Parse existing attributes from a heading text
 * Returns the base text and parsed attributes
 */
export function parseHeadingAttributes(text: string): {
  baseText: string;
  epubType: string | null;
  classes: string[];
  otherAttrs: Record<string, string>;
} {
  const attrRegex = /\s*\{([^}]+)\}\s*$/;
  const match = text.match(attrRegex);

  if (!match) {
    return { baseText: text, epubType: null, classes: [], otherAttrs: {} };
  }

  const baseText = text.replace(attrRegex, '').trim();
  const attrString = match[1];

  let epubType: string | null = null;
  const classes: string[] = [];
  const otherAttrs: Record<string, string> = {};

  // Parse attributes
  const tokenRegex = /#([\w-]+)|\.([^\s.#=]+)|([\w:-]+)=("[^"]*"|'[^']*'|[^\s]+)/g;
  let tokenMatch;

  while ((tokenMatch = tokenRegex.exec(attrString)) !== null) {
    if (tokenMatch[1]) {
      otherAttrs['id'] = tokenMatch[1];
    } else if (tokenMatch[2]) {
      classes.push(tokenMatch[2]);
    } else if (tokenMatch[3] && tokenMatch[4]) {
      let value = tokenMatch[4];
      if ((value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      if (tokenMatch[3] === 'epub:type') {
        epubType = value;
      } else {
        otherAttrs[tokenMatch[3]] = value;
      }
    }
  }

  return { baseText, epubType, classes, otherAttrs };
}

/**
 * Rebuild the attribute string, ensuring epub:type is preserved
 */
export function rebuildAttributeString(
  requiredEpubType: string,
  existingClasses: string[],
  otherAttrs: Record<string, string>
): string {
  const parts: string[] = [];

  // Add ID if present
  if (otherAttrs['id']) {
    parts.push(`#${otherAttrs['id']}`);
  }

  // Add epub:type (required)
  parts.push(`epub:type=${requiredEpubType}`);

  // Add required class (same as epub:type)
  const allClasses = new Set([requiredEpubType, ...existingClasses]);

  // Add classes
  for (const cls of allClasses) {
    parts.push(`.${cls}`);
  }

  // Add other attributes
  for (const [key, value] of Object.entries(otherAttrs)) {
    if (key !== 'id') {
      parts.push(`${key}=${value}`);
    }
  }

  return `{${parts.join(' ')}}`;
}

/**
 * Apply epub:type attributes to the first H1 in a Markdown string.
 * Used when assigning document types outside the editor (e.g. DocsPanel batch).
 * Returns the modified Markdown, or the original if no H1 found or type doesn't need epub:type.
 */
export function applyEpubTypeToMarkdown(markdown: string, documentType: string | null): string {
  if (!documentType) return markdown;

  const epubType = getEpubType(documentType);

  // Find the first H1 line, ensuring a blank line follows it
  const h1Regex = /^(# .+)\n?/m;
  const h1Match = markdown.match(h1Regex);
  if (!h1Match) return markdown;

  const h1Line = h1Match[1];
  const h1FullMatch = h1Match[0];
  const h1Start = h1Match.index!;
  const afterH1 = markdown.slice(h1Start + h1FullMatch.length);
  const needsBlankLine = afterH1.length > 0 && !afterH1.startsWith('\n');

  if (!epubType) {
    // Bodymatter type: remove any existing epub:type attributes from H1
    const parsed = parseHeadingAttributes(h1Line.slice(2)); // skip "# "
    if (parsed.epubType) {
      const remainingClasses = parsed.classes.filter(c => c !== parsed.epubType);
      const otherAttrs = { ...parsed.otherAttrs };
      const parts: string[] = [];
      if (otherAttrs['id']) { parts.push(`#${otherAttrs['id']}`); delete otherAttrs['id']; }
      for (const cls of remainingClasses) parts.push(`.${cls}`);
      for (const [key, value] of Object.entries(otherAttrs)) parts.push(`${key}=${value}`);
      const newH1 = parts.length > 0
        ? `# ${parsed.baseText}  {${parts.join(' ')}}`
        : `# ${parsed.baseText}`;
      const replacement = newH1 + '\n' + (needsBlankLine ? '\n' : '');
      return markdown.slice(0, h1Start) + replacement + afterH1;
    }
    return markdown;
  }

  // Parse existing attributes from H1
  const parsed = parseHeadingAttributes(h1Line.slice(2)); // skip "# "
  const attrStr = rebuildAttributeString(epubType, parsed.classes, parsed.otherAttrs);
  const newH1 = `# ${parsed.baseText}  ${attrStr}`;
  const replacement = newH1 + '\n' + (needsBlankLine ? '\n' : '');

  return markdown.slice(0, h1Start) + replacement + afterH1;
}

// Plugin key for accessing plugin state
export const epubTypePluginKey = new PluginKey('epub-type-plugin');

// Current document type (set from outside)
let currentDocumentType: DocumentType | null = null;

/**
 * Set the current document type for the plugin
 */
export function setDocumentType(docType: DocumentType | null): void {
  currentDocumentType = docType;
}

/**
 * Get the current document type
 */
export function getDocumentType(): DocumentType | null {
  return currentDocumentType;
}

/**
 * Create the epub:type enforcement plugin
 */
export const epubTypePlugin = $prose(() => {
  return new Plugin({
    key: epubTypePluginKey,

    // Append transaction to enforce epub:type on H1
    appendTransaction(transactions, oldState, newState) {
      // Only process if document type requires epub:type
      if (!currentDocumentType || !requiresEpubType(currentDocumentType)) {
        return null;
      }

      const requiredEpubType = getEpubType(currentDocumentType);
      if (!requiredEpubType) return null;

      // Find first H1 in the document
      let h1Pos: number | null = null;
      let h1Node: any = null;

      newState.doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.level === 1 && h1Pos === null) {
          h1Pos = pos;
          h1Node = node;
          return false; // Stop after first H1
        }
        return true;
      });

      if (h1Pos === null || !h1Node) return null;

      // Check if H1 has the required epub:type
      const currentDataAttrs = h1Node.attrs.dataAttrs;
      let hasCorrectEpubType = false;

      if (currentDataAttrs) {
        try {
          const attrs = JSON.parse(currentDataAttrs);
          hasCorrectEpubType = attrs['epub:type'] === requiredEpubType;
        } catch {
          // Invalid JSON, needs update
        }
      }

      // If already correct, no changes needed
      if (hasCorrectEpubType) return null;

      // Build new attributes
      const existingClasses = h1Node.attrs.class ? h1Node.attrs.class.split(' ') : [];
      const newDataAttrs: Record<string, string> = {};

      // Parse existing dataAttrs
      if (currentDataAttrs) {
        try {
          const existing = JSON.parse(currentDataAttrs);
          Object.assign(newDataAttrs, existing);
        } catch {
          // Ignore
        }
      }

      // Set required epub:type
      newDataAttrs['epub:type'] = requiredEpubType;

      // Ensure the epub:type class is present
      const classSet = new Set(existingClasses);
      classSet.add(requiredEpubType);
      const newClass = Array.from(classSet).join(' ');

      // Create transaction to update H1 attributes
      const tr = newState.tr.setNodeMarkup(h1Pos, undefined, {
        ...h1Node.attrs,
        class: newClass,
        dataAttrs: JSON.stringify(newDataAttrs),
      });

      return tr;
    },

    // Filter transactions to prevent removal of epub:type
    filterTransaction(tr, state) {
      // Only filter if document type requires epub:type
      if (!currentDocumentType || !requiresEpubType(currentDocumentType)) {
        return true;
      }

      const requiredEpubType = getEpubType(currentDocumentType);
      if (!requiredEpubType) return true;

      // Check if transaction would remove epub:type from H1
      let wouldRemoveEpubType = false;

      tr.doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.level === 1) {
          const dataAttrs = node.attrs.dataAttrs;
          if (!dataAttrs) {
            wouldRemoveEpubType = true;
            return false;
          }

          try {
            const attrs = JSON.parse(dataAttrs);
            if (attrs['epub:type'] !== requiredEpubType) {
              wouldRemoveEpubType = true;
              return false;
            }
          } catch {
            wouldRemoveEpubType = true;
            return false;
          }
        }
        return true;
      });

      // Allow the transaction but it will be corrected by appendTransaction
      return true;
    },
  });
});

export default {
  epubTypePlugin,
  setDocumentType,
  getDocumentType,
  requiresEpubType,
  getEpubType,
  generateAttributeString,
  parseHeadingAttributes,
  DOCUMENT_TYPE_TO_EPUB_TYPE,
  TYPES_REQUIRING_EPUB_TYPE,
  BODYMATTER_TYPES,
};
